import {
  Phone as PhoneIcon,
  WhatsApp as WhatsAppIcon,
  Language as LanguageIcon,
  Email as EmailIcon,
} from "@mui/icons-material";
import { User } from "../../../store/slice/user-management/types";
import { tokens } from "../../../theme";
import {
  Avatar,
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { MoreVerticalIcon } from "lucide-react";

interface UserCardProps {
  user: User;
  colors: ReturnType<typeof tokens>;
  onView: () => void;
  onMenuClick?: (event: React.MouseEvent<HTMLElement>) => void;
}

function UserCard({ user, colors, onView, onMenuClick }: UserCardProps) {
  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();

  const initials =
    `${user.first_name?.[0] ?? ""}${user.last_name?.[0] ?? ""}`.toUpperCase() ||
    user.username?.[0]?.toUpperCase() ||
    "?";

  const userTypeLabel =
    user.user_type === "company_user"
      ? "Company"
      : user.user_type === "visitor"
        ? "Visitor"
        : "Individual";

  const website = user.company?.website;
  const whatsappDigits = user.phone_number?.replace(/[^\d+]/g, "");

  const contactIcons = [
    {
      key: "phone",
      icon: <PhoneIcon fontSize="small" />,
      color: colors.primary[400],
      href: user.phone_number ? `tel:${user.phone_number}` : undefined,
    },
    {
      key: "whatsapp",
      icon: <WhatsAppIcon fontSize="small" />,
      color: colors.grey[10],
      href: whatsappDigits ? `https://wa.me/${whatsappDigits}` : undefined,
    },
    {
      key: "website",
      icon: <LanguageIcon fontSize="small" />,
      color: colors.grey[200],
      href: website || undefined,
    },
    {
      key: "email",
      icon: <EmailIcon fontSize="small" />,
      color: colors.primary[400],
      href: user.email ? `mailto:${user.email}` : undefined,
    },
  ];

  return (
    <Paper
      onClick={onView}
      sx={{
        p: 3,
        borderRadius: 3,
        textAlign: "center",
        cursor: "pointer",
        position: "relative",
        boxShadow: 0,
      }}
    >
      {onMenuClick && (
        <IconButton
          size="small"
          onClick={onMenuClick}
          sx={{ position: "absolute", top: 8, right: 8 }}
        >
          <MoreVerticalIcon fontSize="small" />
        </IconButton>
      )}

      <Avatar
        src={user.profile_picture_url || undefined}
        sx={{
          width: 96,
          height: 96,
          mx: "auto",
          border: `3px solid ${colors.grey[200]}55`,
          bgcolor: colors.primary[400],
          fontSize: 32,
          fontWeight: 700,
        }}
      >
        {!user.profile_picture_url && initials}
      </Avatar>

      <Typography sx={{ fontWeight: 700, mt: 2 }}>
        {fullName || user.username}
      </Typography>
      <Typography sx={{ color: "text.secondary", fontSize: 14 }}>
        {user.email}
      </Typography>

      <Stack
        direction="row"
        flexWrap="wrap"
        justifyContent="center"
        gap={0.75}
        sx={{ mt: 1.5 }}
      >
        <Chip
          label={userTypeLabel}
          size="small"
          sx={{
            bgcolor: `${colors.grey[200]}22`,
            color: colors.grey[200],
            fontWeight: 600,
          }}
        />
        <Chip label={user.role} size="small" />
        <Chip
          label={user.is_active ? "Active" : "Suspended"}
          size="small"
          color={user.is_active ? "success" : "warning"}
        />
        <Chip
          label={user.is_2fa_enabled ? "2FA On" : "2FA Off"}
          size="small"
          color={user.is_2fa_enabled ? "success" : "default"}
        />
      </Stack>

      <Button
        fullWidth
        variant="contained"
        sx={{ mt: 2, borderRadius: 2 }}
        onClick={(e) => {
          e.stopPropagation();
          onView();
        }}
      >
        VIEW USER
      </Button>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          gap: 2,
          mt: 2,
        }}
      >
        {contactIcons.map(({ key, icon, color, href }) => (
          <IconButton
            key={key}
            size="small"
            component={href ? "a" : "button"}
            href={href}
            target={key === "website" ? "_blank" : undefined}
            rel={key === "website" ? "noopener noreferrer" : undefined}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
            disabled={!href}
            sx={{ color, opacity: href ? 1 : 0.3 }}
          >
            {icon}
          </IconButton>
        ))}
      </Box>
    </Paper>
  );
}

export default UserCard;
